import { useMemo, useState } from 'react'
import {
  Button,
  confirmDialog,
  DataTable,
  DateRangeChip,
  EmptyState,
  FilterChip,
  Money,
  SearchSelect,
  type ColumnDef,
} from '../components'
import { useMe } from '../lib/use-me'
import { canDelete } from '../lib/permissions'
import { useAccounts } from '../lib/api/accounts'
import { useCategories } from '../lib/api/categories'
import { useCounterparties } from '../lib/api/counterparties'
import { useProjects } from '../lib/api/projects'
import { useTags } from '../lib/api/tags'
import {
  flattenTransactions,
  useDeleteTransaction,
  useTransactions,
} from '../lib/api/transactions'
import type {
  Currency,
  Transaction,
  TransactionType,
} from '../lib/api/types'
import type { DrawerTx } from './transaction-form'

export type { DrawerTx } from './transaction-form'

type Props = {
  openDrawer: (tx: DrawerTx) => void
  search: string
  setSearch: (v: string) => void
  accountFilter: string | null
  setAccountFilter: (v: string | null) => void
}

const TYPE_LABEL: Record<TransactionType, string> = {
  income: 'Надходження',
  expense: 'Витрата',
  transfer: 'Переказ',
}

// Amount to show in the table, from the active account's perspective. With an
// account filter a transfer reads as it affects that account: the source leg
// (money leaving) is negative; the destination leg shows the received amount in
// the destination account's own currency, so a cross-currency transfer reads in
// the filtered account's currency rather than the source's. Without a filter
// each row keeps its intrinsic amount (expense negative, everything else plus).
export function displayAmount(
  tx: Transaction,
  accountFilter: string | null,
): { amount: number; currency: Currency } {
  if (accountFilter && tx.type === 'transfer') {
    if (tx.toAccountId === accountFilter && tx.accountId !== accountFilter) {
      return { amount: tx.toAmount ?? tx.amount, currency: tx.toCurrency ?? tx.currency }
    }
    if (tx.accountId === accountFilter) {
      return { amount: -tx.amount, currency: tx.currency }
    }
  }
  return { amount: tx.type === 'expense' ? -tx.amount : tx.amount, currency: tx.currency }
}

// Local input style for the filter bar. The transaction form has its own copy
// in transaction-form.tsx — they share the same look but live in separate
// files now that the form is its own module.
const inputStyle: React.CSSProperties = {
  border: '1px solid var(--border)',
  borderRadius: 4,
  padding: '6px 8px',
  background: 'var(--bg-1)',
  color: 'var(--fg-1)',
  fontSize: 13,
}

export function Transactions({
  openDrawer,
  search,
  setSearch,
  accountFilter,
  setAccountFilter,
}: Props) {
  const me = useMe()
  const allowDelete = canDelete(me.data?.role)

  const accounts = useAccounts()
  const categories = useCategories()
  const projects = useProjects()
  const counterparties = useCounterparties()
  const tags = useTags()

  const [typeFilter, setTypeFilter] = useState<TransactionType | null>(null)
  const [dateFrom, setDateFrom] = useState<string>('')
  const [dateTo, setDateTo] = useState<string>('')
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null)
  const [projectFilter, setProjectFilter] = useState<string | null>(null)
  const [tagFilter, setTagFilter] = useState<string | null>(null)

  const txQuery = useTransactions({
    accountId: accountFilter,
    type: typeFilter ?? undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    categoryId: categoryFilter,
    projectId: projectFilter,
    tagId: tagFilter,
    search: search || undefined,
    limit: 50,
  })

  const items = flattenTransactions(txQuery.data)

  const accountById = useMemo(() => {
    const map = new Map<string, { name: string; currency: Currency }>()
    for (const a of accounts.data ?? []) map.set(a.id, { name: a.name, currency: a.currency })
    return map
  }, [accounts.data])

  const categoryById = useMemo(() => {
    const map = new Map<string, string>()
    for (const c of categories.data ?? []) map.set(c.id, c.name)
    return map
  }, [categories.data])

  const counterpartyById = useMemo(() => {
    const map = new Map<string, string>()
    for (const cp of counterparties.data ?? []) map.set(cp.id, cp.name)
    return map
  }, [counterparties.data])

  const removeTx = useDeleteTransaction()

  const columns: ColumnDef<Transaction>[] = [
    { id: 'date', header: 'Дата', width: 110, sortValue: (tx) => tx.date, cell: (tx) => tx.date },
    {
      id: 'type',
      header: 'Тип',
      width: 100,
      sortValue: (tx) => tx.type,
      cell: (tx) => <span className={`op-pill ${tx.type}`}>{TYPE_LABEL[tx.type]}</span>,
    },
    {
      id: 'account',
      header: 'Рахунок',
      width: 140,
      sortValue: (tx) => accountById.get(tx.accountId)?.name ?? tx.accountId,
      cell: (tx) => accountById.get(tx.accountId)?.name ?? tx.accountId,
    },
    {
      id: 'category',
      header: 'Категорія',
      width: 160,
      sortValue: (tx) => categoryById.get(tx.categoryId) ?? null,
      cell: (tx) => categoryById.get(tx.categoryId) ?? '—',
    },
    {
      id: 'counterparty',
      header: 'Контрагент',
      width: 160,
      sortValue: (tx) => (tx.counterpartyId ? (counterpartyById.get(tx.counterpartyId) ?? null) : null),
      cell: (tx) => (tx.counterpartyId ? (counterpartyById.get(tx.counterpartyId) ?? '—') : '—'),
    },
    {
      id: 'description',
      header: 'Опис',
      sortValue: (tx) => tx.description ?? null,
      cell: (tx) => tx.description ?? '',
    },
    {
      id: 'amount',
      header: 'Сума',
      width: 140,
      thClassName: 'num',
      cellClassName: 'num',
      sortValue: (tx) => displayAmount(tx, accountFilter).amount,
      cell: (tx) => {
        const { amount, currency } = displayAmount(tx, accountFilter)
        return (
          <span className={`amt-pill ${tx.type}`}>
            <Money amount={amount} currency={currency} sign />
          </span>
        )
      },
    },
    {
      id: 'actions',
      header: '',
      width: 100,
      pinned: true,
      cell: (tx) => {
        // Deletion is an owner-only action (the backend enforces `admin` too).
        // It is NOT gated by `source`: editing an imported row is already
        // allowed, so blocking only its deletion was an inconsistent UI limit.
        if (!allowDelete) return null
        const imported = tx.source === 'finmap' || tx.source === 'api'
        return (
          <div style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                void confirmDialog({
                  title: 'Видалити операцію?',
                  body:
                    'Якщо це оплата рахунку, виплата ЗП чи оплата ліцензії — звʼязок буде знято, а суми/статус відкотяться. Дію не можна скасувати.' +
                    (imported
                      ? ' Операцію імпортовано з зовнішнього джерела — повторна синхронізація може створити її знову.'
                      : ''),
                  confirmLabel: 'Видалити',
                  tone: 'danger',
                }).then(async (ok) => {
                  if (!ok) return
                  try {
                    await removeTx.mutateAsync(tx.id)
                  } catch (err) {
                    await confirmDialog({
                      title: 'Не вдалося видалити',
                      body: err instanceof Error ? err.message : 'Невідома помилка',
                      confirmLabel: 'Зрозуміло',
                      cancelLabel: 'Закрити',
                    })
                  }
                })
              }}
            >
              Видалити
            </Button>
          </div>
        )
      },
    },
  ]

  return (
    <>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            placeholder="Пошук в описі…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ ...inputStyle, minWidth: 220 }}
          />
          <SearchSelect
            style={inputStyle}
            width={170}
            value={accountFilter ?? ''}
            onChange={(id) => setAccountFilter(id || null)}
            emptyOption
            emptyLabel="Всі рахунки"
            placeholder="Всі рахунки"
            options={(accounts.data ?? []).map((a) => ({ id: a.id, label: a.name, hint: a.currency }))}
          />
          <select
            value={typeFilter ?? ''}
            onChange={(e) => setTypeFilter((e.target.value as TransactionType) || null)}
            style={inputStyle}
          >
            <option value="">Всі типи</option>
            <option value="income">Надходження</option>
            <option value="expense">Витрата</option>
            <option value="transfer">Переказ</option>
          </select>
          <SearchSelect
            style={inputStyle}
            width={170}
            value={categoryFilter ?? ''}
            onChange={(id) => setCategoryFilter(id || null)}
            emptyOption
            emptyLabel="Всі категорії"
            placeholder="Всі категорії"
            options={(categories.data ?? []).map((c) => ({ id: c.id, label: c.name }))}
          />
          <SearchSelect
            style={inputStyle}
            width={170}
            value={projectFilter ?? ''}
            onChange={(id) => setProjectFilter(id || null)}
            emptyOption
            emptyLabel="Всі проєкти"
            placeholder="Всі проєкти"
            options={(projects.data ?? []).map((p) => ({ id: p.id, label: p.name }))}
          />
          <SearchSelect
            style={inputStyle}
            width={170}
            value={tagFilter ?? ''}
            onChange={(id) => setTagFilter(id || null)}
            emptyOption
            emptyLabel="Всі теги"
            placeholder="Всі теги"
            options={(tags.data ?? []).map((t) => ({ id: t.id, label: t.name }))}
          />
          <DateRangeChip
            from={dateFrom}
            to={dateTo}
            clearable
            onChange={(f, t) => {
              setDateFrom(f)
              setDateTo(t)
            }}
          />
          {(accountFilter ||
            typeFilter ||
            categoryFilter ||
            projectFilter ||
            tagFilter ||
            dateFrom ||
            dateTo ||
            search) && (
            <FilterChip
              label="Очистити"
              onClear={() => {
                setAccountFilter(null)
                setTypeFilter(null)
                setCategoryFilter(null)
                setProjectFilter(null)
                setTagFilter(null)
                setDateFrom('')
                setDateTo('')
                setSearch('')
              }}
            />
          )}
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto' }}>
        {txQuery.isLoading && (
          <div style={{ padding: 20, color: 'var(--fg-3)' }}>Завантаження…</div>
        )}
        {!txQuery.isLoading && items.length === 0 && (
          <div style={{ padding: 40 }}>
            <EmptyState
              icon="receipt"
              title="Транзакцій не знайдено"
              body="Створіть першу операцію або змініть фільтри"
            />
          </div>
        )}
        {items.length > 0 && (
          <DataTable
            tableKey="transactions"
            columns={columns}
            rows={items}
            rowKey={(tx) => tx.id}
            onRowClick={(tx) => openDrawer({ mode: 'edit', transaction: tx })}
          />
        )}
        {txQuery.hasNextPage && (
          <div style={{ padding: 12, textAlign: 'center' }}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => txQuery.fetchNextPage()}
              disabled={txQuery.isFetchingNextPage}
            >
              {txQuery.isFetchingNextPage ? 'Завантаження…' : 'Показати ще'}
            </Button>
          </div>
        )}
      </div>

    </>
  )
}

